import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import communityBannedUsersService from "../../api/services/communityBannedUsersService";
import contentReportsService from "../../api/services/contentReportsService";
import ForceBanUserModal from "./ForceBanUserModal";
import { confirmRestore } from "../../utils/confirmDialog";

const RESTORE_CONFIRM =
  "Delete all of this user’s posts and comments, remove related content reports, and lift the community ban? This cannot be undone.";

function parsePositiveInt(raw) {
  const id = Number(String(raw ?? "").trim());
  if (!Number.isFinite(id) || id < 1) return null;
  return id;
}

export default function BannedUsers() {
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [lookupUserId, setLookupUserId] = useState("");
  const [manualSummary, setManualSummary] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [restoringUserId, setRestoringUserId] = useState(null);
  const [forceBanOpen, setForceBanOpen] = useState(false);

  const canForceBan = useMemo(() => {
    const r = (typeof window !== "undefined" && localStorage.getItem("adminUserRole")) || "";
    const key = String(r).trim().toLowerCase();
    return key === "admin" || key === "developer";
  }, []);

  const loadBannedUsers = useCallback(async (pageNum) => {
    setLoading(true);
    try {
      const data = await communityBannedUsersService.list(pageNum, limit);
      setUsers(data.users || []);
      setTotal(data.total ?? 0);
      setTotalPages(data.totalPages ?? 1);
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Failed to load banned users";
      toast.error(msg);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    loadBannedUsers(page);
  }, [page, loadBannedUsers]);

  /** Stale summary (e.g. different ID) must not leave ban actions enabled. */
  useEffect(() => {
    setManualSummary(null);
  }, [lookupUserId]);

  const lookupIdNum = parsePositiveInt(lookupUserId);
  const anyRowBusy = restoringUserId != null;
  const lookupSummaryMatchesId =
    Boolean(manualSummary?.success) &&
    lookupIdNum != null &&
    Number(manualSummary.user?.id) === lookupIdNum;
  const lookupBanActive = lookupSummaryMatchesId && manualSummary.hasActiveBan === true;

  const loadLookupSummary = async (silent = false) => {
    const id = parsePositiveInt(lookupUserId);
    if (!id) {
      toast.error("Enter a valid app user ID");
      return;
    }
    setLookupLoading(true);
    if (!silent) setManualSummary(null);
    try {
      const data = await contentReportsService.getCommunityModerationSummary(id);
      if (parsePositiveInt(lookupUserId) !== id) return;
      setManualSummary(data);
      if (!silent) toast.success("Loaded status");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load user");
    } finally {
      setLookupLoading(false);
    }
  };

  const restoreUser = async (userId) => {
    if (userId === lookupIdNum && !lookupBanActive) return;
    const ok = await confirmRestore(RESTORE_CONFIRM, "Restore & unban?");
    if (!ok) return;
    setRestoringUserId(userId);
    try {
      const data = await contentReportsService.restoreCommunityAccess(userId);
      toast.success(
        data.message ||
          "Restored: content and reports removed; ban lifted."
      );
      if (parsePositiveInt(lookupUserId) === userId) {
        await loadLookupSummary(true);
      }
      loadBannedUsers(page);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Restore failed");
    } finally {
      setRestoringUserId(null);
    }
  };

  const handleForceBanSuccess = useCallback(async () => {
    setPage(1);
    await loadBannedUsers(1);
  }, [loadBannedUsers]);

  return (
    <div className="p-4 w-full">
      <div className="bg-white rounded-xl shadow-xl p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-md md:text-2xl font-bold text-gray-800">
            Community-banned app users
          </h2>
          {canForceBan && (
            <button
              type="button"
              onClick={() => setForceBanOpen(true)}
              className="shrink-0 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700">
              Force ban user
            </button>
          )}
        </div>

        <p className="text-sm text-gray-600 mb-3">
          Restore removes <strong>all</strong> of the user’s posts and comments, deletes related{" "}
          <strong>content_reports</strong>, and lifts the ban. Row actions or the lookup bar below;{" "}
          <Link to="/content-reports" className="text-red-700 font-medium hover:underline">
            single-report tools
          </Link>{" "}
          stay on Content reports.
          {canForceBan ? (
            <>
              {" "}
              Use <strong>Force ban user</strong> (above) to restrict an account by ID or email without going through
              reports — the list refreshes after a successful ban.
            </>
          ) : null}
        </p>

        <div className="mb-5 p-3 rounded-lg border border-gray-200 bg-gray-50 text-sm">
          <div className="text-xs font-semibold text-gray-700 mb-2">Lookup by user ID</div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <input
              type="number"
              min={1}
              value={lookupUserId}
              onChange={(e) => setLookupUserId(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-1 text-sm w-32 bg-white"
              placeholder="ID"
            />
            <button
              type="button"
              disabled={lookupLoading || !lookupUserId.trim()}
              onClick={() => loadLookupSummary()}
              className="px-2 py-1 rounded-md text-xs font-medium bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-50"
            >
              {lookupLoading ? "…" : "Status"}
            </button>
            <button
              type="button"
              disabled={!lookupBanActive || anyRowBusy}
              onClick={() => lookupIdNum && restoreUser(lookupIdNum)}
              className="px-2 py-1 rounded-md text-xs font-semibold bg-green-700 text-white hover:bg-green-800 disabled:opacity-50"
            >
              {restoringUserId === lookupIdNum ? "…" : "Restore & unban"}
            </button>
          </div>
          {lookupSummaryMatchesId && manualSummary?.success ? (
            <div className="text-xs text-gray-800 space-y-0.5 border-t border-gray-200 pt-2 mt-1">
              <div>
                <span className="text-gray-500">User:</span>{" "}
                {manualSummary.user?.firstName} {manualSummary.user?.lastName}{" "}
                <span className="text-gray-500">({manualSummary.user?.email})</span>
              </div>
              <div>
                <span className="text-gray-500">Ban active:</span>{" "}
                {manualSummary.hasActiveBan ? (
                  <span className="text-red-700 font-medium">Yes</span>
                ) : (
                  <span className="text-green-700">No</span>
                )}
                {manualSummary.hasActiveBan && manualSummary.banIndefinite ? (
                  <span className="text-gray-600 ml-1">(no auto-expiry)</span>
                ) : manualSummary.bannedUntil ? (
                  <span className="text-gray-600 ml-1">
                    until {new Date(manualSummary.bannedUntil).toLocaleString()}
                  </span>
                ) : null}
              </div>
              <div>
                <span className="text-gray-500">Still in app:</span>{" "}
                {manualSummary.unmoderatedPosts ?? 0} posts,{" "}
                {manualSummary.unmoderatedComments ?? 0} comments
              </div>
            </div>
          ) : null}
        </div>

        {loading ? (
          <p className="text-gray-500 py-8 text-center">Loading…</p>
        ) : users.length === 0 ? (
          <p className="text-gray-500 py-8 text-center">No users are currently community-banned.</p>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-700 font-semibold border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2">ID</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Ban</th>
                  <th className="px-3 py-2 min-w-[200px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono">{u.id}</td>
                    <td className="px-3 py-2">
                      {u.firstName || ""} {u.lastName || ""}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{u.email || "—"}</td>
                    <td className="px-3 py-2">
                      {u.banIndefinite ? (
                        <span className="text-red-700 font-medium">Until admin restores</span>
                      ) : u.bannedUntil ? (
                        <span className="text-gray-700">
                          Until {new Date(u.bannedUntil).toLocaleString()}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <button
                          type="button"
                          disabled={anyRowBusy}
                          onClick={() => restoreUser(u.id)}
                          className="px-2 py-0.5 rounded text-[11px] font-semibold bg-green-700 text-white hover:bg-green-800 disabled:opacity-50"
                        >
                          {restoringUserId === u.id ? "…" : "Restore"}
                        </button>
                        <Link
                          to={`/userdetails/${u.id}`}
                          className="px-2 py-0.5 rounded text-[11px] font-medium text-gray-700 border border-gray-300 hover:bg-gray-100"
                        >
                          Profile
                        </Link>
                        <Link
                          to="/content-reports"
                          className="px-2 py-0.5 rounded text-[11px] font-medium text-red-800 border border-red-100 hover:bg-red-50"
                        >
                          Reports
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-2 mt-4 text-sm text-gray-600">
            <span>
              Page {page} of {totalPages} ({total} total)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        <ForceBanUserModal
          isOpen={forceBanOpen}
          onClose={() => setForceBanOpen(false)}
          onSuccess={handleForceBanSuccess}
        />
      </div>
    </div>
  );
}
